import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaShieldAlt } from "react-icons/fa";
import ApiService from "../services/ApiService";
import PageLoader from "../components/PageLoader";
import Header from "../components/Header";

// Dedicated Checkout.com Flow page.
// Reached after an order is created (pending) at /checkout for a new-card payment.
// It creates a server-side payment session for that order, mounts the Flow
// component, and lets Checkout.com handle the card form, wallets and 3DS.
// Fulfillment is done server-side via the /webhook/checkout receiver — this page
// only kicks off the payment and routes the user back to /checkout for the result.
const FlowPayment = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const orderId = searchParams.get("order_id");

  const [pageLoader, setPageLoader] = useState(true);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!orderId) {
      toast.error("Missing order reference.");
      navigate("/checkout");
      return;
    }
    // Guard against React 18 StrictMode double-invocation mounting Flow twice
    if (startedRef.current) return;
    startedRef.current = true;
    initFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initFlow = async () => {
    try {
      if (typeof window.CheckoutWebComponents === "undefined") {
        toast.error("Checkout Flow library not loaded.");
        setPageLoader(false);
        return;
      }

      // 1. Resolve public key + environment.
      //    IMPORTANT: this must match the mode the backend creates the payment
      //    session in — a sandbox session can't be completed by a production/live
      //    Flow component. We read the mode from getAppData (setting.payment_mode)
      //    so both sides stay in sync from one place; fall back to "test".
      const appDataRes = await ApiService.request({
        method: "GET",
        url: "/getAppData",
      });
      const paymentMode = appDataRes.data?.data?.payment_mode || "test";
      const publicKey =
        paymentMode === "live"
          ? process.env.REACT_APP_CHECKOUT_KEY_LIVE
          : process.env.REACT_APP_CHECKOUT_KEY_TEST;

      if (!publicKey) {
        toast.error("Payment configuration missing.");
        setPageLoader(false);
        return;
      }

      // 2. Ask the backend to create a payment session tied to this order.
      //    The backend reads the order's grand_total and attaches metadata.orderId
      //    so the webhook can fulfill the right order once payment succeeds.
      //    Pass success/failure URLs built from the CURRENT origin so the 3DS /
      //    redirect path (triggered when saving a card) returns to this app —
      //    not the production URL baked into the backend env.
      const origin = window.location.origin;
      const basePath = process.env.REACT_APP_BASE_PATH || "";
      const sessionRes = await ApiService.request({
        method: "POST",
        url: "/createPaymentSession",
        data: {
          order_id: orderId,
          success_url: `${origin}${basePath}/checkout?status=succeeded`,
          failure_url: `${origin}${basePath}/checkout?status=failed`,
        },
      });
      const result = sessionRes.data;

      if (!result.status || !result.data) {
        toast.error(result.message || "Could not start payment.");
        navigate("/checkout");
        return;
      }
      const paymentSession = result.data;

      // 3. Initialize Flow with the server-created session
      const checkout = await window.CheckoutWebComponents({
        publicKey,
        environment: paymentMode === "live" ? "production" : "sandbox",
        paymentSession,
        // Match the site's brand green (#006937 / --bs-base). colorAction drives
        // the Pay button background and the selected radio/payment-method accent;
        // colorInverse keeps the button label readable (white on green).
        appearance: {
          colorAction: "#006937",
          colorInverse: "#ffffff",
        },
        onPaymentCompleted: (_component, paymentResponse) => {
          // No-redirect (frictionless) path. The order is fulfilled by the
          // webhook; we just route to /checkout which verifies and confirms.
          const pid = paymentResponse?.id || "";
          navigate(`/checkout?status=succeeded&cko-payment-id=${pid}`);
        },
        onError: (_component, error) => {
          console.error("Flow error:", error);
          toast.error("Payment failed. Please try again.");
          navigate("/checkout?status=failed");
        },
      });

      // 4. Mount the Flow component (card, saved cards, wallets + 3DS handled by CKO).
      //    For 3DS challenges Flow performs a full-page redirect to the order's
      //    success_url / failure_url (/checkout?status=...), handled on that page.
      const flowComponent = checkout.create("flow");
      flowComponent.mount("#flow-container");
    } catch (error) {
      console.error("initFlow error:", error);
      toast.error("Could not initialize payment.");
    } finally {
      setPageLoader(false);
    }
  };

  return (
    <>
      {/* #flow-container must always be in the DOM so flowComponent.mount() can
          find it. PageLoader is a fixed-position overlay shown on top while the
          session is created and Flow is mounting. */}
      {pageLoader && <PageLoader />}

      <div className="main-heading items-details-main container">
        <Header title="Complete Payment" back="/checkout" />

        <div className="container-fluid px-4">
          <div className="row justify-content-center">
            <div className="col-md-6 mt-4">
              <div className="security-notice bg-light border rounded-3 p-3 mb-3 d-flex align-items-center">
                <FaShieldAlt className="text-success me-2 fs-5" />
                <span className="text-muted small">
                  Your payment is encrypted and securely processed by Checkout.com.
                </span>
              </div>

              {/* Flow component mounts here */}
              <div className="border rounded-3 p-3 mb-4">
                <div id="flow-container" className="w-100"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FlowPayment;
